import dayjs from 'dayjs';
import {
  DATE_FORMATS,
  TOY_RESERVATION_DATE_OPERATORS,
  TOY_RESERVATION_DATE_UNITS,
  TOY_RESERVATION_RULE_TYPES,
} from '../../../../common/constants';

export const toUtcDayStartIso = (date) =>
  date ? `${dayjs(date)?.format('YYYY-MM-DD')}T00:00:00.000Z` : null;

export const toUtcDayEndIso = (date) =>
  date ? `${dayjs(date)?.format('YYYY-MM-DD')}T23:59:59.999Z` : null;

export const parseUtcDay = (isoString) => {
  if (!isoString || typeof isoString !== 'string') return null;
  const parsed = dayjs(isoString?.slice(0, 10));
  return parsed?.isValid() ? parsed : null;
};

export const formatRuleDate = (isoString) => {
  const parsed = parseUtcDay(isoString);
  return parsed ? parsed?.format(DATE_FORMATS?.REGULAR) : '-';
};

export const getRuleTypeLabel = (type) =>
  Object.values(TOY_RESERVATION_RULE_TYPES)?.find((item) => item?.value === type)
    ?.label || type || '-';

export const getDateUnitConfig = (unit) =>
  Object.values(TOY_RESERVATION_DATE_UNITS)?.find(
    (item) => item?.value === unit,
  ) || null;

export const getDurationLabel = (duration, unit) => {
  const unitConfig = getDateUnitConfig(unit);
  const count = Number(duration);
  if (!unitConfig || !Number.isFinite(count) || count < 1) return '';
  const label = count === 1 ? unitConfig?.label : unitConfig?.pluralLabel;
  return `${count} ${label?.toLowerCase()}`;
};

const getAccountCreatedSummary = (config) => {
  const operatorLabel =
    Object.values(TOY_RESERVATION_DATE_OPERATORS)?.find(
      (item) => item?.value === config?.op,
    )?.label || config?.op;

  if (config?.op === TOY_RESERVATION_DATE_OPERATORS?.WITHIN_LAST?.value) {
    const durationLabel = getDurationLabel(config?.duration, config?.unit);
    return durationLabel ? `Within last ${durationLabel}` : '-';
  }

  if (config?.op === TOY_RESERVATION_DATE_OPERATORS?.BETWEEN?.value) {
    return `Between ${formatRuleDate(config?.from)} and ${formatRuleDate(
      config?.to,
    )}`;
  }
  return `${operatorLabel || ''} ${formatRuleDate(config?.date)}`?.trim();
};

const getUserTagSummary = (config, tagLabelById = {}) => {
  const tagIds = config?.tagIds || [];
  if (!tagIds?.length) return '-';
  const labels = tagIds?.map((id) => tagLabelById?.[id] || id);
  return labels?.join(` ${config?.logic || 'OR'} `);
};

const getSpecificUserSummary = (config) => {
  const userCount = config?.userIds?.length || 0;
  const contactCount = config?.contactNos?.length || 0;
  const parts = [];
  if (userCount) parts?.push(`${userCount} user${userCount > 1 ? 's' : ''}`);
  if (contactCount) {
    parts?.push(`${contactCount} phone no${contactCount > 1 ? 's' : ''}`);
  }
  return parts?.length ? parts?.join(', ') : '-';
};

const getOrderValueSummary = (config) => {
  const { min, max } = config || {};
  const hasMin = min !== null && min !== undefined;
  const hasMax = max !== null && max !== undefined;
  // min = max = 0 is how the API models the "new users only" case
  if (hasMin && hasMax && Number(min) === 0 && Number(max) === 0) {
    return 'New users only (Rs 0)';
  }
  if (hasMin && hasMax) return `Rs ${min} - Rs ${max}`;
  if (hasMin) return `Min Rs ${min}`;
  if (hasMax) return `Max Rs ${max}`;
  return '-';
};

const getOrderCountSummary = (config) => {
  const { min, max } = config || {};
  const hasMin = min !== null && min !== undefined;
  const hasMax = max !== null && max !== undefined;
  const orders = (count) => `${count} order${Number(count) === 1 ? '' : 's'}`;
  if (hasMin && hasMax && Number(min) === 0 && Number(max) === 0) {
    return 'New users only (0 orders)';
  }
  if (hasMin && hasMax) return `${min} - ${orders(max)}`;
  if (hasMin) return `Min ${orders(min)}`;
  if (hasMax) return `Max ${orders(max)}`;
  return '-';
};

export const getRuleSummary = (rule, tagLabelById = {}) => {
  const config = rule?.config || {};
  switch (rule?.type) {
    case TOY_RESERVATION_RULE_TYPES?.ACCOUNT_CREATED?.value:
      return getAccountCreatedSummary(config);
    case TOY_RESERVATION_RULE_TYPES?.USER_TAG?.value:
      return getUserTagSummary(config, tagLabelById);
    case TOY_RESERVATION_RULE_TYPES?.SPECIFIC_USER?.value:
      return getSpecificUserSummary(config);
    case TOY_RESERVATION_RULE_TYPES?.ORDER_VALUE?.value:
      return getOrderValueSummary(config);
    case TOY_RESERVATION_RULE_TYPES?.ORDER_COUNT?.value:
      return getOrderCountSummary(config);
    default:
      return '-';
  }
};

export const buildRuleConfig = (type, values) => {
  switch (type) {
    case TOY_RESERVATION_RULE_TYPES?.ACCOUNT_CREATED?.value: {
      if (values?.op === TOY_RESERVATION_DATE_OPERATORS?.WITHIN_LAST?.value) {
        return {
          op: values?.op,
          unit: values?.unit,
          duration: Number(values?.duration),
        };
      }
      if (values?.op === TOY_RESERVATION_DATE_OPERATORS?.BETWEEN?.value) {
        return {
          op: values?.op,
          from: toUtcDayStartIso(values?.dateRange?.[0]),
          to: toUtcDayEndIso(values?.dateRange?.[1]),
        };
      }
      return { op: values?.op, date: toUtcDayStartIso(values?.date) };
    }
    case TOY_RESERVATION_RULE_TYPES?.USER_TAG?.value:
      return { tagIds: values?.tagIds || [], logic: values?.logic };
    case TOY_RESERVATION_RULE_TYPES?.SPECIFIC_USER?.value:
      return {
        userIds: values?.userIds || [],
        contactNos: values?.contactNos || [],
      };
    case TOY_RESERVATION_RULE_TYPES?.ORDER_VALUE?.value:
    case TOY_RESERVATION_RULE_TYPES?.ORDER_COUNT?.value:
      return { min: Number(values?.min), max: Number(values?.max) };
    default:
      return {};
  }
};

export const buildRuleFormValues = (rule) => {
  const config = rule?.config || {};
  const base = { name: rule?.name, type: rule?.type, isActive: rule?.isActive };

  switch (rule?.type) {
    case TOY_RESERVATION_RULE_TYPES?.ACCOUNT_CREATED?.value:
      return {
        ...base,
        op: config?.op,
        date: parseUtcDay(config?.date),
        dateRange:
          config?.from && config?.to
            ? [parseUtcDay(config?.from), parseUtcDay(config?.to)]
            : undefined,
        duration: config?.duration ?? undefined,
        unit: config?.unit ?? undefined,
      };
    case TOY_RESERVATION_RULE_TYPES?.USER_TAG?.value:
      return { ...base, tagIds: config?.tagIds || [], logic: config?.logic };
    case TOY_RESERVATION_RULE_TYPES?.SPECIFIC_USER?.value:
      return {
        ...base,
        userIds: config?.userIds || [],
        contactNos: config?.contactNos || [],
      };
    case TOY_RESERVATION_RULE_TYPES?.ORDER_VALUE?.value:
    case TOY_RESERVATION_RULE_TYPES?.ORDER_COUNT?.value:
      return { ...base, min: config?.min, max: config?.max };
    default:
      return base;
  }
};
